/**
 * Button recipes for the video editor's dark surface.
 *
 * The same role `components/buttonStyles.ts` plays for the light side of the
 * app, and separate from it for one reason: those recipes assume a white page.
 * Their fills are `rgba(120,120,128,0.16)` washes and their hovers are
 * darkenings of light colours, neither of which reads on `--editor-bg`.
 *
 * Composed class strings, not components, so a call site keeps its own
 * handlers, testids and layout classes.
 *
 * Every value routes through `--editor-*` or an existing app token. Hover is
 * declared in index.css as `.ebtn-*`, gated on `@media (hover: hover) and
 * (pointer: fine)`, matching how `.btn-sec-wash` and friends work. Declaring
 * hover in a Tailwind class instead would leave a touch press stuck in the
 * hovered state until the coach taps elsewhere.
 */

/* ── Radius ──────────────────────────────────────────────────────────────
   Two values, no more.

   Text buttons are pills at 100, which is FM-5's value and the shape the rest
   of the app uses. Icon buttons take 8, which is `--radius-l` and already the
   editor's own corner: the toolbar chip, the video panel and the annotation
   surfaces are all 8. A pill on a square glyph button reads as a bubble
   rather than a control, so the two shapes stay distinct. */
const PILL = 'rounded-[100px]';
const ICON_R = 'rounded-[8px]';

/* The segmented control is the exception to the two-radius rule.
   The selected item fills the track rather than sitting inside it: no track
   padding, full height, and flush to the ends. Its outer corners are 9 rather
   than the track's 10 because the track's 1px border makes its *inner* radius
   9 — at 10 the fill would overshoot the border by a hair at each corner. That
   is a derived value, not one read off anything. */
const SEG_TRACK_R = 'rounded-[10px]';
const SEG_ITEM_R = 'rounded-none first:rounded-l-[9px] last:rounded-r-[9px]';

/* ── Hit targets ─────────────────────────────────────────────────────────
   8.4: expand the target, do not inflate the visual. A transparent `::after`
   at negative inset takes a small control to 44 without adding a pixel to any
   row's height, which is what `BackButton.tsx` does and why the editor's rows
   keep their geometry through this pass.

   The inset is per size rather than shared, because no single value reaches 44
   from 24, 28 and 32 alike. */
const T = "relative after:absolute after:content-['']";
/** 24 + 10 + 10 = 44. */
const TARGET_24 = `${T} after:-inset-2.5`;
/** 32 + 6 + 6 = 44. */
const TARGET_32 = `${T} after:-inset-1.5`;
/**
 * Vertical only; horizontal is already past 44 at px-4.
 *
 * -6 rather than the -4 that 36 + 4 + 4 = 44 suggests, because `::after` is
 * laid out against the *padding* box. EBTN_SECONDARY's 1px border makes that
 * box 34, not 36, and -4 measured 42 in the built stylesheet. -6 clears 44 for
 * both the bordered and the borderless variants.
 */
const TARGET_TEXT = `${T} after:-inset-y-1.5 after:inset-x-0`;

/* Segmented items expand vertically only, and carry their 44 horizontally as
   real width. Items in a segmented control are contiguous, so a horizontal
   overhang would put each item's target inside its neighbour and a press near
   a seam would land on the wrong option. `SegmentedControl.tsx` makes the same
   call on the light side. */
const SEG_TARGET = `${T} after:inset-x-0 after:-inset-y-2`;

/** 8.4's focus treatment. The editor had none on any of its ~99 buttons. */
const FOCUS =
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-db-accent/40';

/**
 * BASE sets no `background-color` and no `border-width`, deliberately.
 *
 * It used to carry `bg-transparent border-0`, and that silently un-filled
 * every filled variant: `.bg-transparent` and `.border-0` are emitted after
 * `.bg-[var(--color-biscayne)]` and `.border` in the generated stylesheet, and
 * equal-specificity utilities resolve by emission order, not by the order they
 * appear in the class attribute. Start Recording rendered as bare text.
 *
 * The rule that prevents a repeat: BASE never sets a property a variant needs
 * to override. Each variant owns its own background and border outright.
 */
const BASE =
  `${FOCUS} ios-press cursor-pointer disabled:cursor-not-allowed disabled:opacity-40 ` +
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap select-none';

/**
 * Not in BASE, for the reason BASE sets no background either: a call site that
 * needs `flex-1` cannot override it. `.shrink-0` is emitted after `.flex-1`, so
 * BASE's copy won and a text button in a flex row could not shrink at all —
 * `min-w-0` made no difference because the shrink factor, not the min-width,
 * was zero. The icon and segment variants keep it explicitly; the text ones
 * shrink, and their call sites decide.
 */
const NO_SHRINK = 'flex-shrink-0';

/** Text buttons sit at 36, the editor's row scale, not the app's 48. */
const TEXT = `${BASE} ${PILL} ${TARGET_TEXT} h-9 px-4 text-sm font-medium`;

/* ── A note on state colours ─────────────────────────────────────────────
   Where a call site has to override a colour these recipes set (a mute glyph
   turning red, a toggle reading as on), do it with an inline `style`, not with
   another Tailwind class. Two arbitrary-value utilities of the same property
   have equal specificity, so which one wins depends on the order Tailwind
   happens to emit them in, not on the order in the class attribute. An inline
   style is deterministic. */

/* ── Icon sizes ──────────────────────────────────────────────────────────
   Two, because the editor has two icon-button scales: the 36 toolbar chip and
   the 24 rail rows. One shared size would have grown the rail, and every pixel
   of row height comes out of the video. Both target 44. The transport row's
   one control is `EBTN_TRANSPORT` below. */
const iconAt = (px: 24 | 32) => {
  const target = px === 32 ? TARGET_32 : TARGET_24;
  const box = px === 32 ? 'w-8 h-8' : 'w-6 h-6';
  return `${BASE} ${NO_SHRINK} ${ICON_R} ${target} ${box} p-0`;
};

/** Filled brand action. One per surface. */
export const EBTN_PRIMARY = `${TEXT} ebtn-pri bg-[var(--color-biscayne)] text-white`;

/** Sits on a panel: a surface fill with a hairline, so it reads as raised. */
export const EBTN_SECONDARY =
  `${TEXT} ebtn-sec bg-[var(--editor-surface)] border border-[var(--editor-border)] ` +
  'text-[var(--editor-text)]';

/** Text action with no fill until hovered. */
export const EBTN_GHOST = `${TEXT} ebtn-ghost bg-transparent text-[var(--editor-text-muted)]`;

/** Glyph only, 32. Always pair with an aria-label. */
export const EBTN_ICON = `${iconAt(32)} ebtn-icon bg-transparent text-[var(--editor-text-muted)]`;
/** Glyph only, 24, for the rail's dense rows. */
export const EBTN_ICON_XS = `${iconAt(24)} ebtn-icon bg-transparent text-[var(--editor-text-muted)]`;

/**
 * The transport row's control: 44 across, 28 tall.
 *
 * Play is the most-pressed button in the editor and it sat at 28 square with a
 * 12px glyph, reading as the smallest thing on its row. The width is real
 * rather than an `::after` overhang, because the overhang ran under the scrub
 * slider, which is a later sibling and therefore won the overlap.
 */
export const EBTN_TRANSPORT =
  `${BASE} ${NO_SHRINK} ${SEG_TARGET} w-11 h-7 p-0 bg-transparent text-[var(--editor-text)] ebtn-transport`;

/**
 * An icon button that is currently on. Not a variant of its own so much as
 * `EBTN_ICON` in its active state: a surface fill and full-strength text, so
 * the difference from rest is legible without depending on hover.
 */
export const EBTN_ICON_ACTIVE =
  `${iconAt(32)} ebtn-icon bg-[var(--editor-surface)] text-[var(--editor-text)]`;

/**
 * Destructive: a red label on no fill, washing red on hover.
 *
 * Deliberately not a filled red. A filled destructive button competes with the
 * primary for weight, and on both of these headers the primary is the save.
 * Red type plus a wash reads as dangerous and still reads as the lighter of
 * the two.
 */
export const EBTN_DESTRUCTIVE = `${TEXT} ebtn-dest bg-transparent text-[var(--editor-danger)]`;

/** Filled red. The record action only, never for delete. */
export const EBTN_RECORD = `${TEXT} ebtn-rec bg-[var(--editor-danger)] text-white`;

/**
 * Segmented control, for mutually exclusive options.
 *
 * A bounded track with the selected item filling it edge to edge, so the group
 * reads as one control rather than as loose buttons that happen to sit
 * together. The selected state uses the editor's existing selection sub-theme,
 * so no new colour enters.
 *
 * Unlike `SegmentedControl.tsx` on the light side, the selected item is not
 * inset. At this scale a 2px inset reads as a misalignment rather than as
 * padding: it leaves a sliver of track visible around the fill.
 *
 * Mark the container `role="radiogroup"` and each item `role="radio"` with
 * `aria-checked`. The hover CSS keys off `aria-checked`, so a selected item
 * does not wash on hover.
 */
/* h-full, not a fixed height: the item is exactly the track's content box, so
   the selected fill leaves no sliver at top or bottom. Track 32, item 30 inside
   the 1px border, which fits the 36 toolbar row without growing it. */
const SEG_ITEM = `${BASE} ${NO_SHRINK} ${SEG_ITEM_R} ${SEG_TARGET} h-full min-w-11 px-2.5 text-[11px]`;
const SEG_ICON_ITEM = `${BASE} ${NO_SHRINK} ${SEG_ITEM_R} ${SEG_TARGET} w-11 h-full p-0`;

export const EBTN_SEGMENTED = {
  track:
    /* gap-0 and no padding. Any gap would show the track through as a dark line
       beside the selected fill, which is the same sliver at a different angle.
       Adjacent items abut; the fill itself is the separator. */
    `inline-flex items-center h-8 ${SEG_TRACK_R} flex-shrink-0 ` +
    'bg-[var(--editor-bg)] border border-[var(--editor-border)]',
  item: `${SEG_ITEM} ebtn-seg-item bg-transparent font-medium text-[var(--editor-text-muted)]`,
  itemSelected:
    `${SEG_ITEM} font-semibold bg-[var(--editor-selected)] text-[var(--editor-selected-text)]`,
  /** Icon-only segment, for the pen and eraser pair. */
  iconItem: `${SEG_ICON_ITEM} ebtn-seg-item bg-transparent text-[var(--editor-text-muted)]`,
  iconItemSelected:
    `${SEG_ICON_ITEM} bg-[var(--editor-selected)] text-[var(--editor-selected-text)]`,
} as const;

/**
 * The colour swatches' hit target. The swatch keeps its drawn size; this adds
 * a transparent 32 x 44 target around it.
 *
 * 32 across rather than 44: four 44-wide swatches need 176px in a 36-tall row
 * that also carries the tool group, undo, redo and mute, and overlapping
 * targets on a colour picker are worse than a narrow one. Pair it with
 * `gap-3` on the group: the target overhangs a 20px swatch by 6 a side, which
 * a 12px gap consumes exactly, so no swatch's target reaches into its
 * neighbour's.
 *
 * The insets look 2px generous because the swatch's `border-2` shrinks the
 * padding box `::after` is laid out against from 20 to 16. Measured: 32 x 44.
 */
export const EBTN_SWATCH_TARGET =
  `${T} after:-inset-x-2 after:-inset-y-3.5`;

